import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  useTheme,
  Button,
  useMediaQuery,
  Card,
  CardContent,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useSnackbar } from "./app/SnackbarHandler";
import Logger from "./app/Logger";
import { Link } from "react-router-dom";
import CustomInputSwitch from "./app/CustomInputSwitch";
import FeedbackDialog from "./app/FeedbackDialog";
import SchoolIcon from "@mui/icons-material/School";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
const SlotCounter = ({
  value,
  duration = 1500,
  format = (val) => val,
  isDecimal = false,
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const previousValueRef = useRef(0);

  const animationRef = useRef(null);

  useEffect(() => {
    let startTime;
    previousValueRef.current = displayValue;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;

      if (elapsed < duration) {
        // Easing function
        const progress = elapsed / duration;
        const easeOut = 1 - Math.pow(1 - progress, 3);

        const newValue =
          previousValueRef.current +
          (value - previousValueRef.current) * easeOut;
        setDisplayValue(isDecimal ? newValue : Math.round(newValue));

        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(value);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, duration, isDecimal]);

  return format(displayValue);
};
// Simplified teacher plan with monthly/yearly options

const PricesSection = ({
  hideBasic = false,
  strikeThrough = false,
  user,
  styles,
  accountType,
  pricesPage = false,

  showSchoolPlans: externalShowSchoolPlans,
  setShowSchoolPlans: externalSetShowSchoolPlans,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [paymentInterval, setPaymentInterval] = useState("monthly");
  const navigate = useNavigate();
  const [internalShowSchoolPlans, setInternalShowSchoolPlans] = useState(false);
  const showSchoolPlans =
    externalShowSchoolPlans !== undefined
      ? externalShowSchoolPlans
      : internalShowSchoolPlans;
  const setShowSchoolPlans =
    externalSetShowSchoolPlans || setInternalShowSchoolPlans;

  const currentAccountType = accountType || user?.accountType;

  const [openModal, setOpenModal] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { showSnackbar } = useSnackbar();

  const freePlan = {
    title: "GRATIS",
    price: "0 kr",
    period: "/ månad",
    gradient: "linear-gradient(45deg, #4CAF50, #66BB6A)",
    features: [
      "8 genereringar per dag",
      "Tillgång till samtliga funktioner",
      "Prova vår tjänst helt gratis",
      "Uppgradera när som helst",
      "Inget kreditkort krävs",
      "Perfekt för att testa tjänsten",
    ],
    buttonText: "Prova gratiskonto",
    isFree: true,
  };

  const teacherPlan = {
    features:
      currentAccountType === "student"
        ? [
            "Obegränsat antal podcasts",
            "Obegränsat antal quiz",
            "Obegränsat antal flashcards",

            "Obegränsat antal skriftliga prov",
            "Obegränsat antal bedömningar",

            "Obegränsat antal konversationer",
          ]
        : [
            "Obegränsat antal bedömningar",
            "Obegränsat antal lektioner",
            "Obegränsat antal podcasts",
            "Obegränsat antal flashcards",
            "Obegränsat antal quiz",

            "Generera bilder (50 st/mån)",
          ],
    monthlyPrice: {
      price: "99 kr",
      period: "/ månad",
      weeklyPrice: "24,75 kr / vecka",
      priceId: "price_1R54Pr03YgFZTEXDXKsD5c6Y",
      buttonText: pricesPage ? "Skapa konto" : "Välj månatlig plan",
    },
    yearlyPrice: {
      price: "899 kr",
      period: "/ år",
      weeklyPrice: "19,21 kr / vecka",
      priceId: "price_1R54QR03YgFZTEXDfUhygHgq",
      buttonText: pricesPage ? "Skapa konto" : "Välj årlig plan",
    },
    gradient: "linear-gradient(45deg, #7B68EE, #9370DB)",
  };

  const schoolPlans = [
    {
      title: "10 KONTON",
      price: "11900 kr",
      period: "/ år",
      gradient: "#29a19a",
      features: [
        "10 lärarkonton",
        "500 genereringar per konto/mån",
        "50 bildgenereringar per konto/mån",
        "Prioriterad support",
      ],
      buttonText: "Kontakta oss",
    },
    {
      title: "20 KONTON",
      price: "21500 kr",

      period: "/ år",
      gradient: "#7B68EE",
      popular: true,
      features: [
        "20 lärarkonton",
        "500 genereringar per konto/mån",
        "50 bildgenereringar per konto/mån",
        "Prioriterad support",
      ],
      buttonText: "Kontakta oss",
    },
    {
      title: "50 KONTON",
      price: "49500 kr",
      period: "/ år",
      gradient: "#a57215",
      features: [
        "50 lärarkonton",
        "500 genereringar per konto/mån",
        "50 bildgenereringar per konto/mån",
        "Prioriterad support",
      ],
      buttonText: "Kontakta oss",
    },
  ];

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user?.email]);

  const handleSubmit = async () => {
    if (!/\S+@\S+\.\S+/.test(email)) {
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetch(
        `${process.env.NODE_ENV === "production" ? "/api" : "http://localhost:3001/api"}/send-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            message: `Intresseanmälan för ${selectedPlan?.title}`,
            subject: "Intresseanmälan",
          }),
        },
      );

      if (response.ok) {
        setOpenModal(false);
        showSnackbar(
          "Intresseanmälan skickad. Vi hör av oss inom kort!",
          "success",
        );
      }
    } catch (error) {
      console.error(error);
      showSnackbar("Ett fel inträffade. Vänligen försök igen.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine which plans to show
  const currentPlans = showSchoolPlans
    ? schoolPlans
    : [
        ...(pricesPage && paymentInterval === "monthly" ? [freePlan] : []),

        {
          title: paymentInterval === "monthly" ? "MÅNADSVIS" : "ÅRSVIS",
          price:
            paymentInterval === "monthly"
              ? teacherPlan.monthlyPrice.price
              : teacherPlan.yearlyPrice.price,
          period:
            paymentInterval === "monthly"
              ? teacherPlan.monthlyPrice.period
              : teacherPlan.yearlyPrice.period,
          weeklyPrice:
            paymentInterval === "monthly"
              ? teacherPlan.monthlyPrice.weeklyPrice
              : teacherPlan.yearlyPrice.weeklyPrice,
          gradient: teacherPlan.gradient,
          features: teacherPlan.features,
          buttonText:
            paymentInterval === "monthly"
              ? teacherPlan.monthlyPrice.buttonText
              : teacherPlan.yearlyPrice.buttonText,
          popular: true,
          priceId:
            paymentInterval === "monthly"
              ? teacherPlan.monthlyPrice.priceId
              : teacherPlan.yearlyPrice.priceId,
        },
      ];

  const handleTeacherPlanSelection = async (plan) => {
    try {
      setIsSubmitting(true);
      const priceId = plan.priceId;

      const response = await fetch(
        `${process.env.NODE_ENV === "production" ? "" : "http://localhost:3001"}/api/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            priceId,
            userId: user?.uid,
          }),
        },
      );
      Logger.log("priceid: ", priceId);
      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        showSnackbar("Ett fel inträffade. Vänligen försök igen.", "error");
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error:", error);
      showSnackbar("Ett fel inträffade. Vänligen försök igen.", "error");
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      {/* Add the small toggle button below the plans */}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 0,
          mb: showSchoolPlans ? 6 : 3,
        }}
      >
        <Button
          variant="text"
          size="small"
          color="primary"
          onClick={() => setShowSchoolPlans(!showSchoolPlans)}
          startIcon={showSchoolPlans ? <PersonOutlineIcon /> : <SchoolIcon />}
          sx={{
            fontSize: { xs: "0.9rem", md: "1rem" },
            textTransform: "none",
            fontWeight: "500",
            "&:hover": {
              backgroundColor: "rgba(123, 104, 238, 0.08)",
            },
            display: "none",
          }}
        >
          {showSchoolPlans ? "Visa individuella planer" : "Visa skolplaner"}
        </Button>
        <CustomInputSwitch
          optionIcons={[<PersonOutlineIcon />, <SchoolIcon />]}
          selectedIndex={showSchoolPlans ? 1 : 0}
          onIndexChange={(index) => setShowSchoolPlans(index === 1)}
          options={[
            currentAccountType === "teacher" ? "Privatperson" : "Privatperson",
            "Skollicens",
          ]}
          theme={theme}
          width="290px"
          mobileWidth="290px"
          height="35px"
          fontSize={isMobile ? "0.8" : "0.9rem"}
          fontWeight="500"
          borderRadius="14px"
          iconSpacing="8px"
          iconSize="1.1rem"
          activeColor={theme.palette.mode === "dark" ? "#3d3a36" : "#7a7979"}
          nonActiveColor={
            theme.palette.mode === "dark"
              ? "#4a454f"
              : pricesPage
                ? "white"
                : "#f2f1f0"
          }
        />
      </Box>
      {!showSchoolPlans && (
        <>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              fontWeight: "600",
              mt: 1,
              mb: 2,
              fontSize: { xs: "1rem", md: "1.2rem" },
              display: "none",
            }}
          >
            Välj plan:
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: { xs: 5, md: pricesPage ? 8 : 4 },
              mt: 1,
            }}
          >
            <CustomInputSwitch
              selectedIndex={paymentInterval === "monthly" ? 0 : 1}
              onIndexChange={(index) =>
                setPaymentInterval(index === 0 ? "monthly" : "yearly")
              }
              options={["Månadsvis", "Årsvis"]}
              theme={theme}
              width="412px"
              mobileWidth="100%"
              height="50px"
              fontSize={isMobile ? "0.9rem" : "1.1rem"}
              iconSpacing="10px"
              borderRadius="14px"
              showDiscount={true}
              nonActiveColor={
                theme.palette.mode === "dark"
                  ? "#4a454f"
                  : pricesPage
                    ? "white"
                    : "#f2f1f0"
              }
            />
          </Box>
        </>
      )}

      <Grid container spacing={4} justifyContent="center">
        {currentPlans.map((plan, index) => (
          <Grid
            item
            xs={12}
            md={showSchoolPlans ? 4 : pricesPage ? 4.5 : 10}
            key={index}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <Card
                sx={{
                  height: "100%",
                  ...styles.freshBox,
                  ...styles.softBoxShadow,
                  borderRadius: 4,
                  transition: "all 0.3s ease",

                  ...(pricesPage && {
                    "&:hover": {
                      transform: `translateY(${plan.popular ? "-20px" : "-10px"})`,
                      boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
                    },
                  }),
                  position: "relative",
                  ...(plan.popular &&
                    showSchoolPlans && {
                      border: "1px dashed #9370DB",
                      boxShadow: "0 8px 24px rgba(147, 112, 219, 0.15)",
                    }),
                }}
              >
                <CardContent
                  sx={{
                    p: { xs: 0, md: pricesPage && showSchoolPlans ? 1 : 4 },
                    pt: { xs: 0, md: 2 },
                  }}
                >
                  {plan.isFree && (
                    <Box
                      sx={{
                        position: "absolute",
                        right: 10,
                        top: 10,
                        width: "auto",
                        background: "#a1a930",
                        borderRadius: "12px",
                        px: 2,
                        py: 0.5,
                        mb: 2,
                        display: "none",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "white",
                          textAlign: !showSchoolPlans ? "center" : "",
                        }}
                      >
                        Gratiskonto
                      </Typography>
                    </Box>
                  )}
                  <Box
                    sx={{
                      display: !showSchoolPlans ? "none" : "inline-block",
                      width: !showSchoolPlans ? "115px" : "auto",
                      background: plan.gradient,
                      borderRadius: "12px",
                      px: 2,
                      py: 0.5,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: "white",
                        textAlign: !showSchoolPlans ? "center" : "",
                      }}
                    >
                      {plan.title}
                    </Typography>
                  </Box>

                  <Typography
                    variant="h3"
                    sx={{
                      my: 2,

                      fontWeight: "800",
                      textAlign: !showSchoolPlans ? "left" : "",
                      fontSize: { xs: "2rem", md: "3rem" },
                    }}
                  >
                    <SlotCounter
                      value={parseFloat(
                        plan.price.split(" ")[0].replace(",", "."),
                      )}
                      duration={500}
                      format={(value) =>
                        `${value.toFixed(0).replace(".", ",")}`
                      }
                      isDecimal
                    />{" "}
                    kr
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        ml: 1,
                        color: "text.secondary",
                        fontSize: "1.1rem",
                      }}
                    >
                      {plan.period}
                    </Typography>
                  </Typography>
                  {plan.weeklyPrice && (
                    <Typography
                      variant="h3"
                      sx={{
                        mb: 3,
                        fontWeight: "600",
                        display: "none",
                        fontSize: "1.3rem",
                        textAlign: !showSchoolPlans ? "left" : "",
                      }}
                    >
                      <SlotCounter
                        value={parseFloat(
                          plan.weeklyPrice.split(" ")[0].replace(",", "."),
                        )}
                        duration={1500}
                        format={(value) =>
                          `${value.toFixed(2).replace(".", ",")}`
                        }
                        isDecimal
                      />
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          ml: 1,
                          color: "text.secondary",
                          fontSize: "1rem",
                        }}
                      >
                        kr/vecka
                      </Typography>
                    </Typography>
                  )}
                  <Box sx={{ mb: 4, mt: 5 }}>
                    {plan.features.map((feature, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          alignItems: { xs: "left", md: "center" },

                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: showSchoolPlans ? 20 : 25,
                            height: showSchoolPlans ? 20 : 25,
                            borderRadius: "50%",
                            backgroundColor: "rgba(123, 104, 238, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 16, color: "#7B68EE" }} />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "400",
                            fontSize: {
                              xs: "0.9rem",
                              md: showSchoolPlans ? "0.9rem" : "1rem",
                            },
                            textAlign: "left",
                          }}
                        >
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {showSchoolPlans ? (
                    <Button
                      fullWidth
                      variant={
                        theme.palette.mode === "light" ? "purp" : "contained"
                      }
                      color="primary"
                      sx={{ padding: "10px", fontSize: "0.9rem" }}
                      onClick={() => {
                        setSelectedPlan(plan);
                        setOpenModal(true);
                      }}
                    >
                      {plan.buttonText}
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant={plan.isFree ? "print" : "purp"}
                      color="primary"
                      sx={{ padding: "10px", fontSize: "1rem" }}
                      onClick={() => {
                        if (pricesPage === true) {
                          navigate("/app/skapa-konto?ref=startpage");
                        } else {
                          setSelectedPlan(plan);
                          handleTeacherPlanSelection(plan);
                        }
                      }}
                    >
                      {plan.buttonText}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {!pricesPage && !showSchoolPlans && (
        <Box sx={{ justifyContent: "center", mt: 4, display: "" }}>
          <Button
            variant="text"
            size="small"
            color="primary"
            onClick={() => setContactDialogOpen(true)}
            sx={{
              fontSize: "0.95rem",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "rgba(123, 104, 238, 0.08)",
              },
            }}
          >
            Kontakta oss
          </Button>
        </Box>
      )}

      <FeedbackDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        user={user}
        contactForm="true"
      />

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography
            variant="h6"
            component="div"
            sx={{ textAlign: "center", fontWeight: "700", mb: 2 }}
          >
            Intresseanmälan
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              textAlign: "center",
              fontWeight: "400",
              fontSize: "1rem",
              mb: 4,
            }}
          >
            Skriv in din e-postadress så kontaktar vi er fort som möjligt.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="E-postadress"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Button
            variant="print"
            onClick={() => setOpenModal(false)}
            color="primary"
            sx={{ display: "none" }}
          >
            Avbryt
          </Button>
          <Button
            onClick={handleSubmit}
            variant="purp"
            disabled={isSubmitting}
            endIcon={
              isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            Skicka intresseanmälan
          </Button>
        </Box>
      </Dialog>
    </Container>
  );
};

export default PricesSection;
